import React, { useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import { useToasts } from 'react-toast-notifications';

import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import ConfirmModal from '@components/common/Modals/ConfirmModal/ConfirmModal';
import PotService from '@services/domain/Lpc/PotService';
import { getRoute, ROUTE } from '@services/http/Route';

type AcceptModalProps = {
    isVisible: boolean;
    initialTitle: string;
    uniqUP: string;
    potRef: string;
    initialConfirmLabel: string;
    initialCancelLabel: string;
    initialDescription: string;
    cancelLink?: string;
};

const AcceptModal = ({
    isVisible,
    initialTitle,
    uniqUP,
    potRef,
    initialConfirmLabel,
    initialCancelLabel,
    initialDescription,
    cancelLink,
}: AcceptModalProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const router = useRouter();

    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { addToast } = useToasts();

    const [textContent, setTextContent] = useState({
        confirmLabel: initialConfirmLabel,
        cancelLabel: initialCancelLabel,
        description: initialDescription,
        title: initialTitle,
        cancelLink: null,
    });

    const onConfirm = () => {
        setIsLoading(true);
        PotService.acceptOfferedPot(uniqUP)
            .then(() => {
                router.push(getRoute(ROUTE.LPC.POT.POT, potRef)).then();
                setOpenConfirmation(false);
            })
            .catch(() => {
                setIsLoading(true);
                setOpenConfirmation(false);
                addToast(t(`errors.DEFAULT_ERROR_MESSAGE`), {
                    appearance: 'error',
                    autoDismiss: true,
                });
            })
            .finally(() => setIsLoading(false));
    };

    const onCancel = () => {
        PotService.cancelOfferPot(potRef)
            .then(() => {
                setTextContent({
                    title: t('approval.refuse-title'),
                    description: t('approval.refuse-desc'),
                    confirmLabel: '',
                    cancelLabel: t('approval.go-back'),
                    cancelLink: cancelLink,
                });
                !textContent.confirmLabel && setOpenConfirmation(false);
            })
            .catch(() => {
                addToast(t(`errors.DEFAULT_ERROR_MESSAGE`), {
                    appearance: 'error',
                    autoDismiss: true,
                });
            });
    };

    useEffect(() => {
        setOpenConfirmation(isVisible);
    }, [isVisible]);

    return (
        <div>
            <ConfirmModal
                onHide={() => onCancel()}
                onConfirm={() => onConfirm()}
                isLoading={isLoading}
                closeOnOverlayClick={false}
                hideCloseButton
                title={textContent.title}
                text={textContent.description}
                confirmLabel={textContent.confirmLabel}
                cancelLabel={textContent.cancelLabel}
                cancelLink={textContent.cancelLink}
                isVisible={openConfirmation}
                imgUrl={'/front-static/icons/display/info-outline.svg'}
            />
        </div>
    );
};

export default AcceptModal;
